import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, LifeBuoy, Receipt, FolderKanban } from 'lucide-react';
import { Brand, PRODUCT_NAME } from '@/components/shared/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import {
  usePortalAcceptInvite,
  usePortalForgot,
  usePortalLogin,
  usePortalReset,
  usePortalSession,
} from './api';

const PROMISES = [
  { icon: FolderKanban, text: 'Follow your projects and sign off what is delivered' },
  { icon: LifeBuoy, text: 'Raise support tickets and watch the clock on them' },
  { icon: Receipt, text: 'See every invoice, what is paid and what is due' },
];

/**
 * The signed-out frame for the client portal.
 *
 * Same shape as the staff sign-in so the two feel like one product, but the
 * copy is written for a client rather than an employee — nobody arriving here
 * wants to read about permissions and ledgers.
 */
function PortalAuthLayout({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen">
      <aside className="on-navy relative hidden w-[45%] max-w-2xl shrink-0 overflow-hidden bg-sidebar px-14 py-12 lg:flex lg:flex-col">
        <div
          className="absolute -start-24 -top-28 h-[380px] w-[380px] rounded-full bg-primary/20 blur-[110px]"
          aria-hidden
        />
        <div
          className="absolute -bottom-40 -end-16 h-[420px] w-[420px] rounded-full bg-sky-400/[0.14] blur-[120px]"
          aria-hidden
        />

        <div className="relative">
          <Brand tone="dark" />
        </div>

        <div className="relative my-auto">
          <h2 className="max-w-[26rem] text-[2.6rem] font-extrabold leading-[1.06] tracking-tight text-white">
            Your projects, tickets and invoices — all in one place.
          </h2>

          <ul className="mt-8 space-y-3">
            {PROMISES.map((promise) => (
              <li key={promise.text} className="flex items-start gap-2.5">
                <promise.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="text-[0.9375rem] text-white/70">{promise.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative border-t border-white/10 pt-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/40">
            Client portal
          </p>
        </div>
      </aside>

      <main className="flex flex-1 flex-col bg-white px-6 py-8 sm:px-12">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
          <span className="lg:hidden">
            <Brand size="sm" />
          </span>
        </div>

        <div className="mx-auto my-auto w-full max-w-md py-10">
          <h1 className="text-[1.75rem] font-extrabold tracking-tight text-slate-900">{title}</h1>
          {description && <p className="mt-1.5 text-sm text-slate-500">{description}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-6 text-sm text-slate-500">{footer}</div>}
        </div>

        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} {PRODUCT_NAME}
        </p>
      </main>
    </div>
  );
}

export function PortalLoginPage() {
  const { data: session, isLoading } = usePortalSession();
  const login = usePortalLogin();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (!isLoading && session) return <Navigate to="/portal" replace />;

  return (
    <PortalAuthLayout
      title="Sign in to your portal"
      description="Use the email address your account manager invited."
      footer={
        <>
          Are you a member of staff?{' '}
          <Link to="/login" className="font-medium text-link hover:underline">
            Sign in here instead
          </Link>
          .
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          login.mutate({ email, password }, { onSuccess: () => navigate('/portal') });
        }}
      >
        <div>
          <Label htmlFor="portal-email">Email</Label>
          <Input
            id="portal-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="portal-password">Password</Label>
            <Link
              to="/portal/forgot-password"
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              Forgotten it?
            </Link>
          </div>
          <Input
            id="portal-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>

        {login.isError && <FormError message={(login.error as ApiRequestError).message} />}

        <Button type="submit" className="w-full" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </PortalAuthLayout>
  );
}

export function PortalAcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const accept = usePortalAcceptInvite();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <PortalAuthLayout
      title="Set your password"
      description="Choose a password and your portal is ready."
    >
      {!token ? (
        <FormError message="This link is missing its token. Ask for a new invitation." />
      ) : (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            accept.mutate({ token, password }, { onSuccess: () => navigate('/portal') });
          }}
        >
          <div>
            <Label htmlFor="invite-password">Password</Label>
            <Input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              At least ten characters, with an uppercase letter, a lowercase letter and a number.
            </p>
          </div>
          <div>
            <Label htmlFor="invite-confirm">Confirm it</Label>
            <Input
              id="invite-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              required
            />
            {mismatch && <p className="mt-1 text-xs text-destructive">The two do not match.</p>}
          </div>

          {accept.isError && <FormError message={(accept.error as ApiRequestError).message} />}

          <Button type="submit" className="w-full" disabled={accept.isPending || mismatch}>
            Set password and continue
          </Button>
        </form>
      )}
    </PortalAuthLayout>
  );
}

export function PortalForgotPasswordPage() {
  const forgot = usePortalForgot();
  const [email, setEmail] = useState('');

  return (
    <PortalAuthLayout
      title="Reset your password"
      description="We will email you a link if there is an account with that address."
      footer={
        <Link to="/portal/login" className="font-medium text-link hover:underline">
          Back to sign in
        </Link>
      }
    >
      {forgot.isSuccess ? (
        <p className="flex items-start gap-2 rounded-md bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          If that address has a portal account, a reset link is on its way. It is good for an hour.
        </p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            forgot.mutate({ email });
          }}
        >
          <div>
            <Label htmlFor="forgot-email">Email</Label>
            <Input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={forgot.isPending}>
            Send the link
          </Button>
        </form>
      )}
    </PortalAuthLayout>
  );
}

export function PortalResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = usePortalReset();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');

  return (
    <PortalAuthLayout title="Choose a new password">
      {!token ? (
        <FormError message="This link is missing its token. Ask for a new one." />
      ) : reset.isSuccess ? (
        <div className="space-y-4">
          <p className="flex items-start gap-2 rounded-md bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900">
            <Check className="mt-0.5 h-4 w-4 shrink-0" />
            Password changed. Every other session has been signed out.
          </p>
          <Button className="w-full" onClick={() => navigate('/portal/login')}>
            Sign in
          </Button>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            reset.mutate({ token, password });
          }}
        >
          <div>
            <Label htmlFor="reset-password">New password</Label>
            <Input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>
          {reset.isError && <FormError message={(reset.error as ApiRequestError).message} />}
          <Button type="submit" className="w-full" disabled={reset.isPending}>
            Change it
          </Button>
        </form>
      )}
    </PortalAuthLayout>
  );
}
